import { Fragment, ReactNode, useMemo, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListItemButtonWithNavigation from '../input/ListItemButtonWithNavigation'
import { matchPath, useLocation } from 'react-router-dom'
import { useCompactNavigation } from './CompactNavigationContext'

export type DrawerItem = {
  icon?: ReactNode
  text: string
  key: string
  path: string
}

export type DrawerSection = {
  title: string
  id?: string
  items: readonly DrawerItem[]
  dividerBefore?: boolean
}

export type DrawerScaffoldProps = {
  sections: readonly DrawerSection[]
  children: ReactNode
}

const getSectionKey = (section: DrawerSection, index: number) =>
  section.id ?? `${section.title.toLowerCase().replace(/\s+/g, '-')}-${index}`

const DRAWER_WIDTH_PX = 240

const DrawerScaffold = ({ sections, children }: DrawerScaffoldProps) => {
  const { isCompact, drawerOpen, closeDrawer } = useCompactNavigation()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const location = useLocation()

  const isPathActive = (path: string) =>
    !!matchPath(
      {
        path,
        end: false,
      },
      location.pathname
    )

  const grouped = useMemo(() => {
    const visibleSections = sections.filter((section) => section.items.length > 0)
    return visibleSections.map((section, index) => ({
      ...section,
      key: getSectionKey(section, index),
      dividerBefore:
        section.dividerBefore ??
        (index > 0 && visibleSections.slice(0, index).some((prevSection) => prevSection.items.length > 0)),
    }))
  }, [sections])

  const toggle = (key: string, expandedState: boolean) =>
    setExpanded((prev) => ({
      ...prev,
      [key]: expandedState,
    }))

  const handleItemClick = isCompact ? closeDrawer : undefined

  return (
    <Box className="drawerScaffold">
      <Drawer
        variant={isCompact ? 'temporary' : 'permanent'}
        open={isCompact ? drawerOpen : true}
        onClose={isCompact ? closeDrawer : undefined}
        ModalProps={isCompact ? { keepMounted: true } : undefined}
        PaperProps={{ elevation: 2, sx: { width: `${DRAWER_WIDTH_PX}px` } }}
        sx={isCompact ? undefined : { width: `${DRAWER_WIDTH_PX}px`, flexShrink: 0 }}
        className="drawer"
      >
        <Toolbar />
        {grouped.map(({ title, items, dividerBefore, key }) => {
          const sectionHasActiveItem = items.some(({ path }) => isPathActive(path))
          const isExpanded = expanded[key] ?? sectionHasActiveItem
          return (
            <Fragment key={key}>
              {dividerBefore && <Divider />}
              <Accordion
                data-testid={`drawer-section-${key}`}
                expanded={isExpanded}
                onChange={(event, expandedState) => toggle(key, expandedState)}
                disableGutters
                square
                elevation={0}
                sx={{
                  bgcolor: 'transparent',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  data-testid={`drawer-section-toggle-${key}`}
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  aria-controls={`${key}-content`}
                  id={`${key}-header`}
                  sx={{
                    px: 2,
                    py: 0,
                    minHeight: 18,
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center',
                    },
                  }}
                >
                  <ListItemText
                    primary={title}
                    primaryTypographyProps={{
                      variant: 'overline',
                      letterSpacing: 1.5,
                      fontSize: '0.7rem',
                      color: sectionHasActiveItem ? 'primary' : undefined,
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding data-testid={`drawer-section-list-${key}`}>
                    {items.map(({ icon, text, key: itemKey, path }) => (
                      <ListItem key={`item-${itemKey}`} disablePadding data-testid={`drawer-item-${itemKey}`}>
                        <ListItemButtonWithNavigation
                          path={path}
                          dataTestId={`drawer-item-button-${itemKey}`}
                          onClick={handleItemClick}
                        >
                          {icon && <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>}
                          <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                        </ListItemButtonWithNavigation>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Fragment>
          )
        })}
      </Drawer>
      {/* overflowY keeps page overflow inside the content column (drawer and app bar stay put) instead of
          extending the window's scrollable area. Pages that size themselves (flex + minHeight: 0) never
          trigger it and keep managing their own internal scrolling. */}
      <Stack sx={{ flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: 'auto' }}>{children}</Stack>
    </Box>
  )
}

export default DrawerScaffold
